import { Button, DropdownMenu, FormInput } from "@/components/bnp-ui";
import React, { useEffect, useState } from "react";
import Summary from "./Summary";
import ImageUpload from "./ImageUpload";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { PropertyService } from "@/services";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { Switch } from "@mantine/core";
import { getCitiesByState, nigeriaStates, getLatLng } from "@/utils/location";
import PlacesAutocomplete from "./AddressDropDown";
import parseHttpError from "@/utils/parseHttpError";

export const propertyTypeMapping = {
  House: "house",
  "Flat and Apartments": "flat-and-apartments",
  "Semi-Detached Duplex": "semi-detached-duplex",
  "Fully Detached Duplex": "fully-detached-duplex",
  Terraces: "terraces",
  Land: "land",
  "Co-Working Space": "co-working-space",
  Shops: "shops",
  "Commercial Properties": "commercial-properties",
  "Detached Bungalows": "detached-bungalows",
  "Mini Flats": "mini-flats",
  "Self-Contain": "self-contain",
  "Shop in a Mall": "shop-in-a-mall",
  "Office Spaces": "office-spaces",
  Warehouses: "warehouses",
};

const reversePropertyTypeMapping = Object.fromEntries(
  Object.entries(propertyTypeMapping).map(([key, value]) => [value, key]),
);

export function getPropertyType(value: string): string {
  return reversePropertyTypeMapping[value] || "";
}
const Rent = () => {
  const router = useRouter();
  const [step, setStep] = useState(1);
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [isFurnished, setIsFurnished] = useState(false);
  const [isServiced, setIsServiced] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const [isNegotiable, setIsNegotiable] = useState(false);
  const [promoteWeekly, setPromoteWeekly] = useState(false);
  const [requestInspection, setRequestInspection] = useState(false);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [legalFeeHint, setLegalFeeHint] = useState<String>("");
  const [selectedState, setSelectedState] = useState<any>(nigeriaStates[0]);
  const [cityOptions, setCityOptions] = useState<string[]>([]);
  const [latitude, setLatitude] = useState<number | undefined>(undefined);
  const [longitude, setLongitude] = useState<number | undefined>(undefined);

  /////////////////////////////////////////////////////////////////////
  const [files, setFiles] = useState<File[]>([]);

  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);

  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {},
  );

  const [imageLoadingStates, setImageLoadingStates] = useState<
    Record<string, boolean>
  >({});
  /////////////////////////////////////////////////////////////////////

  useEffect(() => {
    if (selectedState) {
      const cities = getCitiesByState(selectedState);
      setCityOptions(cities);
    } else {
      setCityOptions([]);
    }
  }, [selectedState]);

  const defaultValues: any = {
    address: "",
    status: "available",
    listingType: "rent",
    price: undefined,
    propertyType: "",
    // city: "",
    // state: selectedState,
    currency: "",
    furnished: isFurnished,
    serviced: isServiced,
    shared: isShared,
    is_negotiable: isNegotiable,
    bedroomCount: undefined,
    bathroomCount: undefined,
    agencyFees: undefined,
    legalFees: undefined,
    cautionFee: undefined,
    totalPackage: undefined,
    additionalInfo: additionalInfo,
    description: "",
    imageUrl: uploadedUrls,
    promoteWeekly: promoteWeekly,
    requestInspection: requestInspection,
    latitude: undefined,
    longitude: undefined,
  };

  const validationSchema = yup.object().shape({
    address: yup
      .string()
      .required("Address is required")
      .max(100, "Address must be 100 characters or less"),
    status: yup.string().required("Status is required"),
    listingType: yup.string().required("Listing type is required"),
    price: yup
      .number()
      .min(0, "Price must be 0 or more")
      .required("Price is required"),
    currency: yup.string().required("Currency is required"),
    propertyType: yup.string().required("Property type is required"),
    // city: yup
    //   .string()
    //   .max(50, "City must be 50 characters or less")
    //   .required("City is required"),
    // state: yup.string().required("State is required"),
    furnished: yup.boolean().required("Furnished status is required"),
    serviced: yup.boolean().required("Serviced status is required"),
    shared: yup.boolean().required("Shared status is required"),
    is_negotiable: yup.boolean().required("Negotiable status is required"),
    promoteWeekly: yup.boolean().required("Promotion status is required"),
    requestInspection: yup.boolean().required("Inspection status is required"),
    bedroomCount: yup
      .number()
      .required("Bedroom count is required")
      .min(1, "Bedroom count must be 1 or more")
      .max(100, "Bedroom count must be 100 or less"),
    bathroomCount: yup
      .number()
      .required("Bathroom count is required")
      .min(1, "Bathroom count must be 1 or more")
      .max(100, "Bathroom count must be 100 or less"),
    agencyFees: yup
      .number()
      .min(0, "Agency fee must be 0 or more")
      .required("Agency fee is required")
      .test(
        "agency-lte-price",
        "Agency fee cannot exceed the property price",
        function (value) {
          const price = Number(this.parent.price) || 0;
          if (!value || price <= 0) return true;
          return value <= price;
        },
      ),
    legalFees: yup
      .number()
      .typeError("Enter a valid amount")
      .min(0, "Legal fee must be 0 or more")
      .required("Legal fee is required")
      .test(
        "legal-lte-price",
        "Legal fee cannot exceed the property price",
        function (value) {
          const price = Number(this.parent.price) || 0;
          if (!value || price <= 0) return true;
          return value <= price;
        },
      ),
    cautionFee: yup
      .number()
      .min(0, "Caution fee must be 0 or more")
      .required("Caution fee is required")
      .test(
        "caution-lte-price",
        "Caution fee cannot exceed the property price",
        function (value) {
          const price = Number(this.parent.price) || 0;
          if (!value || price <= 0) return true;
          return value <= price;
        },
      ),
    totalPackage: yup.number().required("Total package is required"),
    description: yup
      .string()
      .required("Title is required")
      .matches(
        /^[A-Za-z0-9\s]+$/,
        "Title can only contain letters, numbers and spaces",
      )
      .max(200, "Title must be 200 characters or less"),
    additionalInfo: yup
      .string()
      .max(200, "Additional info must be 200 characters or less"),
    imageUrl: yup.array(),
  });

  const {
    getValues,
    setValue,
    control,
    handleSubmit,
    setError,
    watch,
    trigger,
    formState: { errors, isValid },
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });
  const price = watch("price");
  const agencyFees = watch("agencyFees");
  const legalFees = watch("legalFees");
  const cautionFee = watch("cautionFee");
  const state = watch("state");
  useEffect(() => {
    const basePrice = Number(price) || 0;

    const baseAgencyFee = Number(agencyFees) || 0;

    const baseLegalFee = Number(legalFees) || 0;

    const baseCautionFee = Number(cautionFee) || 0;

    const totalPackage =
      basePrice + baseAgencyFee + baseCautionFee + baseLegalFee;

    setValue("totalPackage", totalPackage, {
      shouldValidate: true,
    });
  }, [price, agencyFees, legalFees, cautionFee, setValue]);

  const handleNext = async () => {
    const isStepValid = await trigger();
    if (isStepValid) {
      setStep(step + 1);
    } else {
      toast.error("Please fill in all required fields correctly");
    }
  };

  const handleFurnishedChange = (value: boolean) => {
    setIsFurnished(value);
    setValue("furnished", value);
  };
  const handleServicedChange = (value: boolean) => {
    setIsServiced(value);
    setValue("serviced", value);
  };

  const handleSharedChange = (value: boolean) => {
    setIsShared(value);
    setValue("shared", value);
  };

  const handleNegotiableChange = (value: boolean) => {
    setIsNegotiable(value);
    setValue("is_negotiable", value);
  };

  const handlePromoteWeekly = (value: boolean) => {
    setPromoteWeekly(value);
    setValue("promoteWeekly", value);
  };

  const handleRequestInspection = (value: boolean) => {
    setRequestInspection(value);
    setValue("requestInspection", value);
  };

  const handleLatLngChange = (lat: number, lng: number) => {
    setLatitude(lat);
    setLongitude(lng);
  };

  const addPropertyRequest = async () => {
    try {
      setValue("additionalInfo", additionalInfo);
      setValue("imageUrl", uploadedUrls);
      const address = getValues().address;
      let lat = latitude;
      let lng = longitude;
      if (address && (lat === undefined || lng === undefined)) {
        const coords = await getLatLng(address);
        lat = coords.lat;
        lng = coords.lng;
      }
      setValue("latitude", lat);
      setValue("longitude", lng);
      const response = await PropertyService.AddProperty(getValues());
      return response?.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const mutation: any = useMutation({
    mutationFn: addPropertyRequest,
    onSuccess: (res: any) => {
      // console.log(res?.data);
      toast.success("Property added!");
      router.reload();
    },
    onError: (error: any) => {
      const msg = (error.message || "").toLowerCase();
      if (
        msg.includes("realtor not found") ||
        msg.includes("business not found") ||
        msg.includes("do not have permission")
      ) {
        toast.error(
          "Please complete your onboarding before listing a property.",
        );
        setTimeout(() => router.push("/auth/onboarding"), 2000);
      } else {
        toast.error(error.message || "Request failed");
      }
    },
  });

  const onSubmit = handleSubmit(
    (data) => {
      mutation.mutate();
    },
    (errors) => {
      toast.error("Please check all required fields");
      console.log(errors);
    },
  );

  return (
    <div>
      {step === 1 && (
        <form
          action={"submit"}
          onSubmit={(e) => e.preventDefault()}
          className="border-b border-soft-200 mb-4 pb-4"
        >
          <div className="w-[100%]flex flex-col lg:items-center lg:flex-row gap-3 mb-4">
            <Controller
              name="description"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div>
                  <FormInput
                    type="text"
                    name="description"
                    value={value}
                    label="Title"
                    placeholder="e.g. Modern Two Bedroom Apartment in Abuja"
                    className="w-[100%]  h-[48px]"
                    onChange={onChange}
                    required
                  />
                  {errors?.description && (
                    <p className="text-red-500 text-sm">
                      {errors?.description?.message as string}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
          <div className="flex flex-col lg:items-center lg:flex-row gap-3">
            <PlacesAutocomplete
              control={control}
              errors={errors}
              setLatLng={handleLatLngChange}
            />
            {/* <div className="items-center  flex flex-col lg:flex-row gap-3">
              <Controller
                name="state"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="w-[100%] lg:w-[185px] lg:max-w-[185px] flex flex-col gap-1">
                    <p className="text-sm font-medium text-main-900">
                      State <span className="text-red-500 text-sm">*</span>
                    </p>
                    <DropdownMenu
                      name={"state"}
                      value={value}
                      options={nigeriaStates}
                      onChange={(e) => {
                        const newState = e.target.value;
                        setSelectedState(newState);
                        setValue("state", newState);
                        setValue("city", "");
                      }}
                    />
                    {errors?.state && (
                      <p className="text-red-500 text-sm">
                        {errors?.state?.message as string}
                      </p>
                    )}
                  </div>
                )}
              />

              <Controller
                name="city"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="w-[100%] lg:w-[185px] lg:max-w-[185px] flex flex-col gap-1">
                    <p className="text-sm font-medium text-main-900">
                      City <span className="text-red-500 text-sm">*</span>
                    </p>
                    <DropdownMenu
                      name={"city"}
                      value={value}
                      options={cityOptions}
                      onChange={onChange}
                    />
                    {errors?.city && (
                      <p className="text-red-500 text-sm">
                        {errors?.city?.message as string}
                      </p>
                    )}
                  </div>
                )}
              />
            </div> */}

            <div className="mt-[-5px] flex w-[100%] lg:w-[50%] flex-col lg:items-center lg:flex-row gap-3">
              <Controller
                name="propertyType"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="w-[100%] lg:w-[50%] flex flex-col gap-1">
                    <p className="text-sm font-medium text-main-900">
                      Property type{" "}
                      <span className="text-red-500 text-sm">*</span>
                    </p>
                    <DropdownMenu
                      name={"propertyType"}
                      value={value}
                      options={[
                        "House",
                        "Flat and Apartments",
                        "Semi-Detached Duplex",
                        "Fully Detached Duplex",
                        "Terraces",
                        "Land",
                        "Co-Working Space",
                        "Shops",
                        "Commercial Properties",
                        "Detached Bungalows",
                        "Mini Flats",
                        "Self-Contain",
                        "Shop in a Mall",
                        "Office Spaces",
                        "Warehouses",
                      ]}
                      onChange={(e) => {
                        //@ts-ignore
                        const apiValue = propertyTypeMapping[e.target.value];
                        onChange(apiValue);
                      }}
                    />
                    {errors?.propertyType && (
                      <p className="text-red-500 text-sm">
                        {errors?.propertyType?.message as string}
                      </p>
                    )}
                  </div>
                )}
              />

              <div className="items-center flex flex-col lg:flex-row gap-3">
                <div className="">
                  <p className="text-sm  text-main-900 font-medium">
                    Furnished? <span className="text-red-500 text-sm">*</span>
                  </p>
                  <div className="flex gap-2 items-center mt-4 ">
                    <div className="flex items-center gap-2 w-[81.5px]">
                      <label>Yes</label>
                      <input
                        type="radio"
                        checked={isFurnished}
                        onChange={() => handleFurnishedChange(true)}
                        name="furnished"
                      />
                    </div>

                    <div className="flex items-center gap-2 w-[81.5px]">
                      <label>No</label>
                      <input
                        type="radio"
                        checked={!isFurnished}
                        onChange={() => handleFurnishedChange(false)}
                        name="furnished"
                      />
                    </div>
                  </div>
                  {errors?.furnished && (
                    <p className="text-red-500 text-sm">
                      {errors?.furnished?.message as string}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="w-[100%] items-center flex flex-col lg:flex-row gap-3">
            <Controller
              name="bedroomCount"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="mt-2 w-[100%] lg:w-1/3">
                  <FormInput
                    type="number"
                    value={value}
                    name={"bedroomCount"}
                    label="Bedroom count"
                    placeholder="Bedroom count"
                    className="w-[100%]   h-[48px]"
                    required
                    onChange={onChange}
                  />

                  {errors?.bedroomCount && (
                    <p className="text-red-500 text-sm">
                      {errors?.bedroomCount?.message as string}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="bathroomCount"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="mt-2 w-[100%] lg:w-1/3">
                  <FormInput
                    type="number"
                    value={value}
                    name={"bathroomCount"}
                    label="Bathroom count"
                    placeholder="Bathroom count"
                    className="w-[100%] h-[48px]"
                    required
                    onChange={onChange}
                  />

                  {errors?.bathroomCount && (
                    <p className="text-red-500 text-sm">
                      {errors?.bathroomCount?.message as string}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="price"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="mt-2 w-[100%] lg:w-1/3">
                  <FormInput
                    type="number"
                    value={value}
                    name="price"
                    label="Price"
                    placeholder="Enter price"
                    className="w-[100%]   h-[48px]"
                    required
                    onChange={onChange}
                  />
                  {errors?.price && (
                    <p className="text-red-500 text-sm">
                      {errors?.price?.message as string}
                    </p>
                  )}
                </div>
              )}
            />
          </div>

          <div className="w-[100%] mt-4 flex flex-col lg:items-center lg:flex-row gap-3">
            <div className="w-[100%] lg:w-1/4">
              <p className="text-sm text-main-900 font-medium">
                Serviced? <span className="text-red-500 text-sm">*</span>
              </p>
              <div className="flex gap-2 items-center mt-4">
                <div className="flex items-center gap-2 w-[81.5px]">
                  <label>Yes</label>
                  <input
                    type="radio"
                    checked={isServiced}
                    onChange={() => handleServicedChange(true)}
                    name="serviced"
                  />
                </div>
                <div className="flex items-center gap-2 w-[81.5px]">
                  <label>No</label>
                  <input
                    type="radio"
                    checked={!isServiced}
                    onChange={() => handleServicedChange(false)}
                    name="serviced"
                  />
                </div>
              </div>
              {errors?.serviced && (
                <p className="text-red-500 text-sm">
                  {errors?.serviced?.message as string}
                </p>
              )}
            </div>

            <div className=" w-[100%] lg:w-2/4 flex flex-col lg:items-center lg:flex-row gap-3">
              <div className="w-[100%] lg:w-[50%]">
                <p className="text-sm text-main-900 font-medium">
                  Shared? <span className="text-red-500 text-sm">*</span>
                </p>
                <div className="flex gap-2 items-center mt-4">
                  <div className="flex items-center gap-2 w-[81.5px]">
                    <label>Yes</label>
                    <input
                      type="radio"
                      checked={isShared}
                      onChange={() => handleSharedChange(true)}
                      name="shared"
                    />
                  </div>
                  <div className="flex items-center gap-2 w-[81.5px]">
                    <label>No</label>
                    <input
                      type="radio"
                      checked={!isShared}
                      onChange={() => handleSharedChange(false)}
                      name="shared"
                    />
                  </div>
                </div>
                {errors?.shared && (
                  <p className="text-red-500 text-sm">
                    {errors?.shared?.message as string}
                  </p>
                )}
              </div>

              <div className="w-[100%] lg:w-[50%] items-center flex flex-col lg:flex-row gap-3">
                <div className="mt-[-10px]">
                  <p className="text-sm text-main-900 font-medium">
                    Price Negotiable?{" "}
                    <span className="text-red-500 text-sm">*</span>
                  </p>
                  <div className="flex gap-2 items-center mt-4">
                    <div className="flex items-center gap-2 w-[81.5px]">
                      <label>Yes</label>
                      <input
                        type="radio"
                        checked={isNegotiable}
                        onChange={() => handleNegotiableChange(true)}
                        name="is_negotiable"
                      />
                    </div>
                    <div className="flex items-center gap-2 w-[81.5px]">
                      <label>No</label>
                      <input
                        type="radio"
                        checked={!isNegotiable}
                        onChange={() => handleNegotiableChange(false)}
                        name="is_negotiable"
                      />
                    </div>
                  </div>
                  {errors?.negotiable && (
                    <p className="text-red-500 text-sm">
                      {errors?.negotiable?.message as string}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <Controller
              name="currency"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="w-[100%] lg:w-1/4 flex flex-col gap-1">
                  <p className="text-sm font-medium text-main-900">
                    Currency <span className="text-red-500 text-sm">*</span>
                  </p>
                  <DropdownMenu
                    name={"currency"}
                    value={value}
                    options={["NGN", "USD", "GBP"]}
                    onChange={onChange}
                  />
                  {errors?.currency && (
                    <p className="text-red-500 text-sm">
                      {errors?.currency?.message as string}
                    </p>
                  )}
                </div>
              )}
            />
          </div>

          <div className="flex flex-col lg:items-start lg:flex-row gap-3 mb-4">
            <Controller
              name="agencyFees"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="mt-5 w-[100%] lg:w-1/4">
                  <FormInput
                    type="number"
                    value={value}
                    name="agencyFees"
                    label="Agency fee"
                    placeholder="Enter fee"
                    className="w-[100%]  h-[48px]"
                    onChange={onChange}
                    disabled={promoteWeekly}
                    required
                  />
                  {errors?.agencyFees && (
                    <p className="text-red-500 text-sm">
                      {errors?.agencyFees?.message as string}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="legalFees"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="mt-5 w-[100%] lg:w-1/4">
                  <FormInput
                    type="text"
                    value={value}
                    name="legalFees"
                    label="Legal fee"
                    placeholder="Enter fee or percentage e.g. 30%"
                    className="w-[100%]  h-[48px]"
                    onChange={(e: any) => {
                      const raw = String(e?.target?.value ?? e ?? "");
                      const percentMatch = raw
                        .replace(/\s/g, "")
                        .match(/^(\d+(?:\.\d+)?)%$/);
                      if (percentMatch) {
                        const percent = parseFloat(percentMatch[1]);
                        const propertyPrice = Number(price) || 0;
                        const computed = Math.round(
                          (propertyPrice * percent) / 100,
                        );
                        onChange(computed);
                        setLegalFeeHint(
                          propertyPrice > 0
                            ? `${percent}% of ₦${propertyPrice.toLocaleString()} = ₦${computed.toLocaleString()}`
                            : "Enter property price first to convert percentage",
                        );
                      } else {
                        onChange(e);
                        setLegalFeeHint("");
                      }
                    }}
                    required
                  />
                  {legalFeeHint && (
                    <p className="text-green-600 text-sm mt-1">
                      {legalFeeHint}
                    </p>
                  )}
                  {errors?.legalFees && (
                    <p className="text-red-500 text-sm">
                      {errors?.legalFees?.message as string}
                    </p>
                  )}
                </div>
              )}
            />
            <Controller
              name="cautionFee"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="mt-5 w-[100%] lg:w-1/4">
                  <FormInput
                    type="number"
                    value={value}
                    name="cautionFee"
                    label="Caution fee"
                    placeholder="Enter fee"
                    className="w-[100%]  h-[48px]"
                    onChange={onChange}
                    required
                  />

                  {errors?.cautionFee && (
                    <p className="text-red-500 text-sm">
                      {errors?.cautionFee?.message as string}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="totalPackage"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="mt-5 w-[100%] lg:w-1/4">
                  <FormInput
                    type="number"
                    value={value}
                    name="totalPackage"
                    label="Total package"
                    placeholder="Enter price"
                    className="w-[100%]  h-[48px]"
                    required
                    disabled
                    onChange={onChange}
                  />

                  {errors?.totalPackage && (
                    <p className="text-red-500 text-sm">
                      {errors?.totalPackage?.message as string}
                    </p>
                  )}
                </div>
              )}
            />
          </div>

          <Switch
            checked={promoteWeekly}
            className="cursor-pointer"
            label={
              <div className="flex flex-col gap-1 mt-[-4px]">
                <p className="text-base font-medium text-main-900">
                  Promote to weekly deals
                </p>
                <p className="text-sm text-grey-7">
                  Listings pushed to weekly deals connote that customers will
                  not be paying agency fee on the transactions.
                </p>
              </div>
            }
            onChange={() => {
              handlePromoteWeekly(!promoteWeekly);
              if (!promoteWeekly) {
                setValue("agencyFees", 0);
              }
            }}
          />
          <Switch
            checked={requestInspection}
            className="cursor-pointer mt-5"
            label={
              <div className="flex flex-col gap-1 mt-[-4px]">
                <p className="text-base font-medium text-main-900">
                  Request Live Inspection
                </p>
                <p className="text-sm text-grey-7">
                  This will enable this property to be available for live
                  inspection requests to be carried out on behalf of the
                  potential tenants.
                </p>
              </div>
            }
            onChange={() => {
              handleRequestInspection(!requestInspection);
            }}
          />
        </form>
      )}

      <div className={step === 2 ? "block" : "hidden"}>
        <ImageUpload
          additionalInfo={additionalInfo}
          setAdditionalInfo={setAdditionalInfo}
          uploadedUrls={uploadedUrls}
          setUploadedUrls={setUploadedUrls}

          files={files}
          setFiles={setFiles}

          uploadingFiles={uploadingFiles}
          setUploadingFiles={setUploadingFiles}

          uploadProgress={uploadProgress}
          setUploadProgress={setUploadProgress}

          imageLoadingStates={imageLoadingStates}
          setImageLoadingStates={setImageLoadingStates}

          additionalInfoView
        />
      </div>

      {step === 3 && (
        <Summary
          uploadedUrls={uploadedUrls}
          price={getValues().price}
          bedroomCount={getValues().bedroomCount}
          bathroomCount={getValues().bathroomCount}
          propertyType={getValues().propertyType}
          furnished={isFurnished ? "Yes" : "No"}
          city={getValues().city}
          address={getValues().address}
          legalFees={getValues().legalFees}
          agencyFees={getValues().agencyFees}
          cautionFee={getValues().cautionFee}
          totalPackage={getValues().totalPackage}
          shared={isShared ? "Yes" : "No"}
          serviced={isServiced ? "Yes" : "No"}
          negotiable={isNegotiable ? "Yes" : "No"}
          currency={getValues().currency}
        />
      )}
      <div className="flex w-[100%] justify-between">
        <div className="flex items-center gap-[10px]">
          <div
            className={`w-2 h-2 rounded-full transition-all ${
              step === 1 ? "bg-bnp-cyan" : "bg-soft-200"
            }`}
          ></div>
          <div
            className={`w-2 h-2 rounded-full transition-all ${
              step === 2 ? "bg-bnp-cyan" : "bg-soft-200"
            }`}
          ></div>
          <div
            className={`w-2 h-2 rounded-full transition-all ${
              step === 3 ? "bg-bnp-cyan" : "bg-soft-200"
            }`}
          ></div>
        </div>
        <div className="flex gap-3 items-center mb-8">
          {step > 1 && (
            <Button
              className="bg-white border border-border-1 text-bnp-dark text-sm font-medium w-[94px] h-[60px] rounded-lg"
              onClick={() => {
                setStep(step - 1);
              }}
            >
              Back
            </Button>
          )}

          {step !== 3 && (
            <Button
              className="bg-bnp-dark text-white text-sm font-medium w-[94px] h-[60px] rounded-lg"
              onClick={handleNext}
              disabled={false}
            >
              Next
            </Button>
          )}

          {step === 3 && (
            <Button
              className="bg-bnp-dark text-white text-sm font-medium w-[94px] h-[60px] rounded-lg"
              onClick={onSubmit}
              // onClick={()=>{console.log(getValues())}}
              isLoading={mutation.isPending}
            >
              Submit
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Rent;
